import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH, HOME_PATH } from '@/constants/paths';
import { useUserStore } from '@hooks/useUserStore';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { monitorNetworkStatus } from '@/components/utils/utils';
import { parseQueryParams } from '@/utils/QueryParamsRoutes/queryParamsUtils';

export interface RouteProps {
  children: React.ReactNode;
  isProtected?: boolean;
  fromRegistration?: boolean;
}

export const Route = ({
  children,
  isProtected,
  fromRegistration,
}: RouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: userData } = useUserStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasCheckedPath, setHasCheckedPath] = useState(false);

  useEffect(() => {
    if (!hasCheckedPath) {
      const originalPath = `${window.location.pathname}${window.location.search}`;
      if (originalPath.includes('%3F') || originalPath.includes('%3f')) {
        const decodedPath = decodeURIComponent(originalPath);
        navigate(decodedPath, { replace: true });
      }
      setHasCheckedPath(true);
    }
  }, [navigate, hasCheckedPath]);

  useEffect(() => {
    monitorNetworkStatus((value) => setIsOnline(value));
  }, []);

  if (!hasCheckedPath) {
    return null;
  }

  const queryParams = parseQueryParams(location.search);
  const { redirect_path: redirectPath } = queryParams;
  const deepLinkRedirection = redirectPath ? redirectPath : HOME_PATH;

  if (!isOnline) {
    return <ErrorLoadingPage errorType='offline' hideNavBar />
  }

  if (isProtected && !userData) {
    return <Navigate to={LOGIN_PATH} state={{ from: location }} replace />;
  } else if (fromRegistration && userData) {
    return children;
  } else if (!isProtected && userData) {
    return <Navigate to={deepLinkRedirection} replace />;
  }

  return children;
};
